if (!customElements.get("f-cart-goal")) {
  class FCartGoal extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartDrawer: ".f-cart-drawer__inner",
        cartGoalTitle: ".f-cartgoal__title",
        progressBar: ".f-cartgoal__bar span",
      };

      this.confettiShow = false;

      this.money_format = window.FoxThemeSettings.money_format;
      this.enableConfetti = this.dataset.enableConfetti === "true";

      // Thresholds
      this.gift1Goal = Number(this.dataset.gift1Goal || 59);
      this.shippingGoal = Number(
        this.dataset.shippingGoal || this.dataset.cartGoal || 79
      );
      this.gift2Goal = Number(this.dataset.gift2Goal || 99);

      // Backend-controlled gift availability
      this.gift1Enabled = this.dataset.gift1Enabled === "true";
      this.gift2Enabled = this.dataset.gift2Enabled === "true";

      // Reward row content
      this.gift1Name = this.dataset.gift1Name || "";
      this.gift2Name = this.dataset.gift2Name || "";
      this.gift1Image = this.dataset.gift1Image || "";
      this.gift2Image = this.dataset.gift2Image || "";

      // Build only the goals that actually exist
      this.goals = [];

      if (this.gift1Enabled) {
        this.goals.push({
          type: "gift1",
          goal: this.gift1Goal,
          label: "gift",
        });
      }

      if (this.shippingGoal > 0) {
        this.goals.push({
          type: "shipping",
          goal: this.shippingGoal,
          label: "free shipping",
        });
      }

      if (this.gift2Enabled) {
        this.goals.push({
          type: "gift2",
          goal: this.gift2Goal,
          label: "second gift",
        });
      }

      this.goals.sort((a, b) => a.goal - b.goal);
      this.maxGoal = this.goals.length
        ? this.goals[this.goals.length - 1].goal
        : 0;
    }

    connectedCallback() {
      this.renderMilestones();
      this.updateCartGoal(Number(this.dataset.cartTotal));

      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, (state) => {
        this.updateCartGoal(state.items_subtotal_price);
      });

      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
        const newCart = await window.FoxThemeCartHelpers.getState();
        if (newCart) {
          this.updateCartGoal(newCart.items_subtotal_price);
        }
      });

      document.addEventListener("cart:reward-refresh", () => {
        window.FoxThemeCartHelpers.getState().then((newCart) => {
          if (newCart) {
            this.updateCartGoal(newCart.items_subtotal_price);
          }
        });
      });
    }

    getMilestonePosition(index) {
      if (!this.goals.length) return 0;
      if (this.goals.length === 1) return 100;

      // Evenly spread dots like before
      return ((index + 1) / this.goals.length) * 100;
    }

    getVisualPercent() {
      if (!this.goals.length) return 0;
      if (this.cartTotal <= 0) return 0;

      // Piecewise progress:
      // Each goal-to-goal range fills only the visual segment between those two dots.
      for (let i = 0; i < this.goals.length; i++) {
        const currentGoal = this.goals[i].goal;
        const currentPos = this.getMilestonePosition(i);

        const prevGoal = i === 0 ? 0 : this.goals[i - 1].goal;
        const prevPos = i === 0 ? 0 : this.getMilestonePosition(i - 1);

        if (this.cartTotal <= currentGoal) {
          const range = currentGoal - prevGoal || 1;
          const progressInSegment = (this.cartTotal - prevGoal) / range;
          const clampedProgress = Math.max(
            0,
            Math.min(progressInSegment, 1)
          );

          return prevPos + clampedProgress * (currentPos - prevPos);
        }
      }

      return 100;
    }

    renderMilestones() {
      const bar = this.querySelector(".f-cartgoal__bar");
      if (!bar || bar.querySelector(".f-cartgoal__milestones")) return;

      const milestonesWrap = document.createElement("div");
      milestonesWrap.className = "f-cartgoal__milestones";

      if (!this.goals.length) {
        bar.appendChild(milestonesWrap);
        return;
      }

      this.goals.forEach((goalItem, index) => {
        const marker = document.createElement("span");
        marker.className = "f-cartgoal__milestone";
        marker.dataset.goal = goalItem.goal;
        marker.dataset.type = goalItem.type;

        const position = this.getMilestonePosition(index);
        marker.style.left = `${position}%`;

        if (position === 100) {
          marker.style.transform = "translate(-100%, -50%)";
        }

        milestonesWrap.appendChild(marker);
      });

      bar.appendChild(milestonesWrap);
    }

    updateCartGoal(state) {
      if (state > 0) {
        this.classList.remove("hidden");
      } else {
        this.classList.add("hidden");
      }

      this.cartTotal = state / 100;

      const titleEl = this.querySelector(this.selectors.cartGoalTitle);

      const gift1Unlocked =
        this.gift1Enabled && this.cartTotal >= this.gift1Goal;
      const shippingUnlocked =
        this.shippingGoal > 0 && this.cartTotal >= this.shippingGoal;
      const gift2Unlocked =
        this.gift2Enabled && this.cartTotal >= this.gift2Goal;

      let message = "";
      let nextGoal = null;
      let nextGoalType = null;

      const nextGoalItem = this.goals.find(
        (goalItem) => this.cartTotal < goalItem.goal
      );

      if (nextGoalItem) {
        nextGoal = nextGoalItem.goal;
        nextGoalType = nextGoalItem.type;

        if (nextGoalType === "gift1") {
          message = `Spend <span data-left-to-spend></span> more to unlock a gift`;
        } else if (nextGoalType === "shipping") {
          message = `Spend <span data-left-to-spend></span> more for free shipping`;
        } else if (nextGoalType === "gift2") {
          message = `Spend <span data-left-to-spend></span> more to unlock a second gift`;
        }
      } else {
        if (this.gift1Enabled || this.gift2Enabled) {
          message = `Congratulations! You've collected your gifts & free shipping`;
        } else {
          message = `Congratulations! You've unlocked free shipping`;
        }
      }

      if (titleEl) {
        titleEl.innerHTML = message;
      }

      let visualPercent = this.getVisualPercent();
      visualPercent = Math.max(0, Math.min(visualPercent, 100));

      this.percent = visualPercent;
      this.style.setProperty("--progress", `${this.percent}%`);
      this.dataset.total = this.cartTotal;

      if (nextGoal !== null) {
        const spend = Math.max((nextGoal - this.cartTotal) * 100, 0);
        const leftToSpendEl = this.querySelector("[data-left-to-spend]");

        if (leftToSpendEl) {
          leftToSpendEl.innerHTML = formatMoney(spend, this.money_format);
        }

        this.classList.remove("f-cartgoal--done", "shakeY");
      } else {
        this.classList.add("f-cartgoal--done", "shakeY");
      }

      this.updateMilestoneStates();
      this.updateRewardRow(gift1Unlocked, gift2Unlocked);

      if (gift2Unlocked && this.enableConfetti) {
        this.showConfetti();
      } else {
        this.confettiShow = false;
      }
    }

    updateMilestoneStates() {
      const milestones = this.querySelectorAll(".f-cartgoal__milestone");

      milestones.forEach((milestone) => {
        const goal = Number(milestone.dataset.goal);

        if (this.cartTotal >= goal) {
          milestone.classList.add("is-unlocked");
        } else {
          milestone.classList.remove("is-unlocked");
        }
      });
    }

    updateRewardRow(gift1Unlocked, gift2Unlocked) {
      const drawer = this.closest(".f-cart-drawer, .f-cart-drawer__inner, .drawer");
      const row = drawer
        ? drawer.querySelector("[data-cart-reward-row]")
        : document.querySelector("[data-cart-reward-row]");
      const imageEl = drawer
        ? drawer.querySelector("[data-cart-reward-image]")
        : document.querySelector("[data-cart-reward-image]");
      const textEl = drawer
        ? drawer.querySelector("[data-cart-reward-text]")
        : document.querySelector("[data-cart-reward-text]");

      if (!row || !imageEl || !textEl) return;

      if (this.gift1Enabled && !gift1Unlocked) {
        const spend = Math.max((this.gift1Goal - this.cartTotal) * 100, 0);

        row.classList.remove("hidden");

        if (this.gift1Image) {
          imageEl.src = this.gift1Image;
          imageEl.alt = this.gift1Name;
          imageEl.style.display = "";
        } else {
          imageEl.removeAttribute("src");
          imageEl.alt = "";
          imageEl.style.display = "none";
        }

        textEl.innerHTML = `Spend <strong>${formatMoney(
          spend,
          this.money_format
        )}</strong> more to get a free ${this.gift1Name || "gift"}`;

        return;
      }

      if (this.gift2Enabled && !gift2Unlocked) {
        const spend = Math.max((this.gift2Goal - this.cartTotal) * 100, 0);

        row.classList.remove("hidden");

        if (this.gift2Image) {
          imageEl.src = this.gift2Image;
          imageEl.alt = this.gift2Name;
          imageEl.style.display = "";
        } else {
          imageEl.removeAttribute("src");
          imageEl.alt = "";
          imageEl.style.display = "none";
        }

        textEl.innerHTML = `Spend <strong>${formatMoney(
          spend,
          this.money_format
        )}</strong> more to get a free ${this.gift2Name || "gift"}`;

        return;
      }

      row.classList.add("hidden");
    }

    showConfetti() {
      const cartDrawer = this.closest(this.selectors.cartDrawer);
      const cartTemplate = this.closest(".cart-template");

      if (!this.confettiShow && (cartDrawer || cartTemplate)) {
        setTimeout(() => {
          if (cartDrawer) {
            new window.FoxTheme.Confetti(cartDrawer);
          }
          if (cartTemplate) {
            new window.FoxTheme.Confetti(document.body);
          }
          this.confettiShow = true;
        }, 800);
      }
    }
  }

  customElements.define("f-cart-goal", FCartGoal);
}